import { type ReactNode, useRef } from "react";
import { useCoverTransform } from "@/hooks/useCoverTransform";

type CoverCanvasProps = {
    /** Original image width */
    imageW: number;
    /** Original image height */
    imageH: number;
    /** Additional classes on the outer container */
    className?: string;
    children: ReactNode;
};

/**
 * CoverCanvas - Container that replicates object-fit:cover via transforms.
 *
 * Children are rendered inside a div sized to imageW × imageH,
 * then scaled/offset to cover the parent. This means children can
 * position themselves using the original image coordinate space.
 */
export function CoverCanvas({ imageW, imageH, className = "", children }: CoverCanvasProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scale, offsetX, offsetY } = useCoverTransform(containerRef, imageW, imageH);

    return (
        <div ref={containerRef} className={`overflow-hidden ${className}`}>
            <div
                style={{
                    position: "absolute",
                    width: imageW,
                    height: imageH,
                    transformOrigin: "0 0",
                    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
                    willChange: "transform",
                }}
            >
                {children}
            </div>
        </div>
    );
}